import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BlockIcon from '@mui/icons-material/Block';
import Business from '@mui/icons-material/Business';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DangerousIcon from '@mui/icons-material/Dangerous';
import DescriptionIcon from '@mui/icons-material/Description';
import ErrorIcon from '@mui/icons-material/Error';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import GavelIcon from '@mui/icons-material/Gavel';
import HandshakeIcon from '@mui/icons-material/Handshake';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SendIcon from '@mui/icons-material/Send';
import ShareIcon from '@mui/icons-material/Share';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { alpha, Box, Chip, Grid, Stack } from '@mui/material';
import { getBusinessesSummary } from 'lib/api/dashboard';
import { ParsedBusinessData } from 'lib/api/dashboard/types';
import StatCard from 'lib/designSystem/Charts/StatCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { colors } from 'lib/designSystem/Table/cellFormat';
import Title from 'lib/designSystem/Title';
import { BASE_COLORS } from 'lib/functions/const/colors';
import { errorHandler } from 'lib/functions/errors';
import { formatMoney, showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

type Summary = ParsedBusinessData | undefined;
type Props = { businessIdentifier: string };

const tgrColorMap: Record<ParsedBusinessData['tgrAlert'], string> = {
  'Mega peligro': BASE_COLORS.red,
  Peligro: BASE_COLORS.orange,
  Advertencia: BASE_COLORS.yellow,
  'Sin alertas': BASE_COLORS.green,
};

const tgrIconMap: Record<ParsedBusinessData['tgrAlert'], React.ReactNode> = {
  'Mega peligro': <DangerousIcon />,
  Peligro: <ErrorIcon />,
  Advertencia: <WarningAmberIcon />,
  'Sin alertas': <CheckCircleIcon />,
};

const DataHubSummary = (props: Props) => {
  const { businessIdentifier } = props;
  const dispatch = useDispatch();
  const [summary, setSummary] = useState<Summary>();
  const [loading, setLoading] = useState(false);

  const connection = colors[summary?.statusLabel || ''] ?? BASE_COLORS.gray;
  const tgrColor = tgrColorMap[summary?.tgrAlert || 'Sin alertas'];

  const fetchSummary = async () => {
    try {
      setLoading(true);
      if (!businessIdentifier) {
        showAlert(dispatch, 'error', 'No se ha encontrado el id de la empresa');
        return;
      }
      const query = `?page=1&pageSize=10&businessIdentifier=${businessIdentifier}`;
      const response = await getBusinessesSummary(dispatch, query);
      setSummary(response?.data[0]);
    } catch (error) {
      errorHandler(dispatch, error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchSummary();
  }, []);

  if (loading)
    return (
      <Box minHeight="200px">
        <CenteredCircularProgress />
      </Box>
    );

  if (!summary) return null;

  return (
    <Box mt={2}>
      <Stack direction="row" alignItems="center" spacing={2} ml={1}>
        <Title
          title={`${summary.name} - ${summary.identifier}`}
          icon={<Business sx={{ color: BASE_COLORS.blue }} />}
          color={BASE_COLORS.blue}
        />
        <Chip
          label={summary.statusLabel}
          style={{
            backgroundColor: alpha(connection, 0.2),
            color: connection,
            fontWeight: 'bold',
            fontSize: '12px',
            opacity: 1,
            width: '110px',
            marginBottom: '10px',
          }}
        />
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Prom. Venta últimos 6 meses"
            value={formatMoney(summary.averageSalesLast6Months)}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<TrendingUpIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Prom. Venta últimos 12 meses"
            value={formatMoney(summary.averageSalesLast12Months)}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<TrendingUpIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="% Notas de crédito"
            value={`${(summary.creditNotesPercentage * 100).toFixed(2)}%`}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<CreditCardIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Monto Notas de crédito"
            value={formatMoney(summary.creditNotesAmount)}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<ReceiptIcon />}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="% Cancelaciones"
            value={`${(summary.cancellationPercentage * 100).toFixed(2)}%`}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<CancelIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Monto Cancelaciones"
            value={formatMoney(summary.cancellationAmount)}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<BlockIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Deuda fiscal vencida"
            value={formatMoney(summary.fiscalDebtDue)}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<AccountBalanceIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Facturas cruzadas"
            value={formatMoney(summary.crossInvoiceTotal)}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<CompareArrowsIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Cedido últimos 6 meses"
            value={formatMoney(summary.ownCessionLast6MonthsAmount)}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<SendIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Cedido último año"
            value={formatMoney(summary.ownCessionLast12MonthsAmount)}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<SendIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Cedido a otros últimos 6 meses"
            value={formatMoney(summary.othersCessionLast6MonthsAmount)}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<ShareIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Cedido a otros último año"
            value={formatMoney(summary.othersCessionLast12MonthsAmount)}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<ShareIcon />}
          />
        </Grid>
      </Grid>

      <Stack direction="row" alignItems="center" spacing={2} ml={1} mt={3}>
        <Title
          title="Estado TGR"
          icon={
            <Box
              sx={{
                color: tgrColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
              }}
            >
              {tgrIconMap[summary.tgrAlert]}
            </Box>
          }
          color={tgrColor}
        />
        <Chip
          label={summary.tgrAlert}
          style={{
            backgroundColor: alpha(tgrColor, 0.2),
            color: tgrColor,
            fontWeight: 'bold',
            fontSize: '12px',
            opacity: 1,
            width: '110px',
            marginBottom: '10px',
          }}
        />
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Folios impagos"
            value={summary.unpaidFoliosCount}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<DescriptionIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Folios vencidos"
            value={summary.overdueFoliosCount}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<EventBusyIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Deuda fiscal vencida"
            value={formatMoney(summary.fiscalDebtDue)}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<AccountBalanceIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Convenios"
            value={summary.activeAgreementsCount}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<HandshakeIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Cuotas impagas"
            value={summary.unpaidDebtsCuotasCount}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<PaymentIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Total de procesos"
            value={summary.totalProceedings}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<GavelIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Procesos en embargos"
            value={summary.proceedingFIds}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<GavelIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Procesos en notificación"
            value={summary.proceedingNIds}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<NotificationsIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            label="Procesos en inicio"
            value={summary.proceedingIIds}
            valueLabel=""
            color={BASE_COLORS.gray}
            icon={<GavelIcon />}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default DataHubSummary;
